/**
 * Timezone Utilities
 * 
 * Utilities for handling timezone conversions and formatting
 * using date-fns-tz for lightweight, efficient operations.
 */

import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { enUS, ar } from 'date-fns/locale';
import type { TimezoneOption } from '../types';

/**
 * Common timezones for the trading platform
 * Ordered by relevance to trading
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  // Recommended
  { value: 'UTC', label: 'UTC (Trading Standard)', offset: '+00:00', recommended: true },
  
  // Middle East (+03:00 to +04:00)
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: '+04:00' },
  { value: 'Asia/Riyadh', label: 'Riyadh (AST)', offset: '+03:00' },
  
  // Americas (-08:00 to -03:00)
  { value: 'America/New_York', label: 'New York (EST/EDT)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', offset: '-08:00' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', offset: '-03:00' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)', offset: '-03:00' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', offset: '-06:00' },
  
  // Europe (+00:00 to +02:00)
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Oslo', label: 'Oslo (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)', offset: '+01:00' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)', offset: '+02:00' },
  { value: 'Europe/Helsinki', label: 'Helsinki (EET/EEST)', offset: '+02:00' },
  { value: 'Europe/Kyiv', label: 'Kyiv (EET/EEST)', offset: '+02:00' },
  { value: 'Europe/Bucharest', label: 'Bucharest (EET/EEST)', offset: '+02:00' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)', offset: '+03:00' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', offset: '+03:00' },
  
  // Africa (+02:00)
  { value: 'Africa/Cairo', label: 'Cairo (EET)', offset: '+02:00' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', offset: '+02:00' },
  
  // South Asia (+05:00 to +05:30)
  { value: 'Asia/Karachi', label: 'Karachi (PKT)', offset: '+05:00' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Delhi (IST)', offset: '+05:30' },
  
  // Southeast Asia (+06:00 to +07:00)
  { value: 'Asia/Dhaka', label: 'Dhaka (BST)', offset: '+06:00' },
  { value: 'Asia/Almaty', label: 'Almaty (ALMT)', offset: '+06:00' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', offset: '+07:00' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh (ICT)', offset: '+07:00' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)', offset: '+07:00' },
  
  // East Asia (+08:00 to +09:00)
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', offset: '+08:00' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', offset: '+08:00' },
  { value: 'Asia/Taipei', label: 'Taipei (CST)', offset: '+08:00' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', offset: '+09:00' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+09:00' },
  
  // Oceania (+10:00 to +12:00)
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: '+10:00' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)', offset: '+10:00' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)', offset: '+10:00' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', offset: '+08:00' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)', offset: '+12:00' },
  { value: 'Pacific/Fiji', label: 'Fiji (FJT)', offset: '+12:00' },
  { value: 'Pacific/Noumea', label: 'Noumea (NCT)', offset: '+11:00' },
];

/**
 * Get date-fns locale from language code
 */
export function getDateLocale(language: string) {
  const langPrefix = language.split('-')[0];
  switch (langPrefix) {
    case 'ar':
      return ar;
    case 'en':
    default:
      return enUS;
  }
}

/**
 * Format a date in a specific timezone
 * 
 * @param date - The date to format (can be Date, ISO string, or timestamp)
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @param formatStr - date-fns format string
 * @param language - Language code for locale-aware formatting
 */
export function formatDateInTimezone(
  date: Date | string | number,
  timezone: string,
  formatStr: string = 'PPpp', // "Apr 29, 2023, 12:00 AM"
  language: string = 'en-US'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  // Validate date - return placeholder for invalid dates
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '—';
  }
  
  return formatInTimeZone(dateObj, timezone, formatStr, {
    locale: getDateLocale(language),
  });
}

/**
 * Clock format type
 */
export type ClockFormat = '12h' | '24h';

/**
 * Format a date for trading display
 * Shows time with timezone abbreviation
 * 
 * @param date - The date to format
 * @param timezone - IANA timezone identifier
 * @param language - Language code
 * @param clockFormat - 12-hour or 24-hour format (default: 24h)
 */
export function formatTradingTime(
  date: Date | string | number,
  timezone: string = 'UTC',
  language: string = 'en-US',
  clockFormat: ClockFormat = '24h'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  // Validate date - return placeholder for invalid dates
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '—';
  }
  
  // Format: "14:32:45 UTC" (24h) or "2:32:45 PM UTC" (12h)
  const timeFormat = clockFormat === '12h' ? 'h:mm:ss a zzz' : 'HH:mm:ss zzz';
  return formatInTimeZone(dateObj, timezone, timeFormat, {
    locale: getDateLocale(language),
  });
}

/**
 * Format a date for display in trade history
 * 
 * @param date - The date to format
 * @param timezone - IANA timezone identifier
 * @param language - Language code
 * @param clockFormat - 12-hour or 24-hour format (default: 24h)
 */
export function formatTradeDate(
  date: Date | string | number,
  timezone: string = 'UTC',
  language: string = 'en-US',
  clockFormat: ClockFormat = '24h'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  // Validate date - return placeholder for invalid dates
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '—';
  }
  
  // Format: "Jan 15, 2026 14:32 UTC" (24h) or "Jan 15, 2026 2:32 PM UTC" (12h)
  const dateFormat = clockFormat === '12h' ? 'MMM d, yyyy h:mm a zzz' : 'MMM d, yyyy HH:mm zzz';
  return formatInTimeZone(dateObj, timezone, dateFormat, {
    locale: getDateLocale(language),
  });
}

/**
 * Get the current time in a specific timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  return toZonedTime(new Date(), timezone);
}

/**
 * Convert a local time to UTC
 */
export function localToUTC(localDate: Date, timezone: string): Date {
  return fromZonedTime(localDate, timezone);
}

/**
 * Convert UTC to a local timezone
 */
export function utcToLocal(utcDate: Date, timezone: string): Date {
  return toZonedTime(utcDate, timezone);
}

/**
 * Get the user's local timezone from the browser
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format timezone offset for display (e.g., "+04:00")
 */
export function formatTimezoneOffset(timezone: string): string {
  const now = new Date();
  const formatted = formatInTimeZone(now, timezone, 'xxx'); // +04:00 format
  return formatted;
}

/**
 * Get a timezone option by value
 */
export function getTimezoneOption(value: string): TimezoneOption | undefined {
  return COMMON_TIMEZONES.find(tz => tz.value === value);
}

/**
 * Check if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}
